import React from 'react';

/**
 * FieldRenderer — renders the input control for a single questionnaire field.
 *
 * Used by both QuestionnairePage (public) and ConsultationPage (authenticated).
 * Keeps field rendering logic in one place.
 *
 * Props:
 *   field    {object}   Questionnaire field schema object (fieldKey, type, options, isRequired)
 *   value    {*}        Current answer value
 *   onChange {function} (fieldKey, newValue) => void
 *   disabled {boolean}  Optional — render read-only (e.g. review mode)
 */
const FieldRenderer = ({ field, value, onChange, disabled = false }) => {
  const base = { disabled };

  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          value={value ?? ''}
          onChange={(e) => onChange(field.fieldKey, e.target.value)}
          required={field.isRequired}
          {...base}
        />
      );

    case 'radio':
      return (
        <div className="radio-group">
          {(field.options || []).map((opt) => (
            <label key={opt} className="radio-option">
              <input
                type="radio"
                name={field.fieldKey}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(field.fieldKey, opt)}
                required={field.isRequired}
                {...base}
              />
              {opt}
            </label>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <div className="checkbox-group">
          {(field.options || []).map((opt) => (
            <label key={opt} className="checkbox-option">
              <input
                type="checkbox"
                value={opt}
                checked={Array.isArray(value) ? value.includes(opt) : false}
                onChange={(e) => {
                  const current = Array.isArray(value) ? value : [];
                  const updated = e.target.checked
                    ? [...current, opt]
                    : current.filter((v) => v !== opt);
                  onChange(field.fieldKey, updated);
                }}
                {...base}
              />
              {opt}
            </label>
          ))}
        </div>
      );

    case 'select':
      return (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(field.fieldKey, e.target.value)}
          required={field.isRequired}
          {...base}
        >
          <option value="">-- Select --</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );

    case 'rating':
      return (
        <div className="rating-group">
          {[1, 2, 3, 4, 5].map((n) => (
            <label key={n} className={`rating-option${value === n ? ' selected' : ''}`}>
              <input
                type="radio"
                name={field.fieldKey}
                value={n}
                checked={value === n}
                onChange={() => onChange(field.fieldKey, n)}
                {...base}
              />
              {n}
            </label>
          ))}
        </div>
      );

    default: // 'text' | 'date'
      return (
        <input
          type={field.type === 'date' ? 'date' : 'text'}
          value={value ?? ''}
          onChange={(e) => onChange(field.fieldKey, e.target.value)}
          required={field.isRequired}
          {...base}
        />
      );
  }
};

export default FieldRenderer;
